import React, { useState } from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '11px',
  fontWeight: 600,
  margin: '2px',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const SiteCodesDisplay = ({ selectedSites, maxVisible = 3, showCount = true }) => {
  const [showAll, setShowAll] = useState(false);

  if (!selectedSites || selectedSites.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#667085', fontStyle: 'italic' }}>
        No sites selected
      </Typography>
    );
  }

  const siteCodes = selectedSites.map((site) => site.site_code).filter(Boolean);
  const totalCount = siteCodes.length;
  const visibleCodes = showAll ? siteCodes : siteCodes.slice(0, maxVisible);
  const remainingCount = totalCount - maxVisible;

  // Generate consistent colors for site codes
  const getSiteCodeColor = (siteCode) => {
    const colors = [
      '#E3F2FD',
      '#F3E5F5',
      '#E8F5E8',
      '#FFF3E0',
      '#FCE4EC',
      '#E0F2F1',
      '#F1F8E9',
      '#FFF8E1',
      '#E3F2FD',
      '#F3E5F5',
    ];
    const hash = siteCode.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const getSiteCodeTextColor = (siteCode) => {
    const textColors = [
      '#1976D2',
      '#7B1FA2',
      '#388E3C',
      '#F57C00',
      '#C2185B',
      '#00695C',
      '#689F38',
      '#F9A825',
      '#1976D2',
      '#7B1FA2',
    ];
    const hash = siteCode.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return textColors[Math.abs(hash) % textColors.length];
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {/* Show count if requested */}
      {showCount && (
        <Typography
          variant="body2"
          sx={{
            color: '#344054',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {totalCount} site{totalCount !== 1 ? 's' : ''} selected:
        </Typography>
      )}

      {/* Display site code chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {visibleCodes.map((siteCode, index) => (
          <Tooltip key={siteCode} title={`Site Code: ${siteCode}`} arrow placement="top">
            <StyledChip
              label={siteCode}
              size="small"
              sx={{
                backgroundColor: getSiteCodeColor(siteCode),
                color: getSiteCodeTextColor(siteCode),
                border: `1px solid ${getSiteCodeTextColor(siteCode)}20`,
                '&:hover': {
                  backgroundColor: getSiteCodeTextColor(siteCode),
                  color: 'white',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          </Tooltip>
        ))}

        {/* Show more/less button if there are more sites */}
        {totalCount > maxVisible && (
          <Tooltip
            title={showAll ? 'Show fewer sites' : `Show all ${totalCount} sites`}
            arrow
            placement="top"
          >
            <StyledChip
              label={showAll ? 'Show Less' : `+${remainingCount} more`}
              size="small"
              clickable
              onClick={() => setShowAll(!showAll)}
              sx={{
                backgroundColor: '#F5F5F5',
                color: '#667085',
                border: '1px solid #D0D5DD',
                '&:hover': {
                  backgroundColor: '#E5E5E5',
                  color: '#344054',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default SiteCodesDisplay;
